import { UserService } from './../../user/services/user.service';
import { Shop } from './../types/shop.type';
import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LocalService } from '../../../services/storage/local.service';
import { parse, isBefore, isAfter } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class ShopService {
  private readonly SHOP_KEY = 'selectedShop';
  private readonly SHOPCODE_KEY = 'shopcode';
  private readonly selectedShop = signal<Shop>(null);
  private readonly selectedShopCodeSignal = signal<string>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly localService: LocalService,
    private readonly userService: UserService,
  ) {}

  initShop() {
    if (this.localService.getPlainObjData(this.SHOP_KEY) == undefined) {
      const storedShop = this.userService.getUser()?.defaultShop;
      this.localService.savePlainObjData(this.SHOP_KEY, storedShop);
    }

    if (this.localService.getItem(this.SHOPCODE_KEY) == undefined) {
      const storedShopCode = this.userService.getUser()?.defaultShop?.shopCode;
      this.localService.setItem(this.SHOPCODE_KEY, storedShopCode);
    }
  }

  getAllShop(): Observable<Shop[]> {
    return this.http.get<Shop[]>(environment.api_url + 'all/shop');
  }

  getMarketplaceShops(): Observable<Shop[]> {
    return this.http.get<Shop[]>(environment.api_url + 'shop/marketplace');
  }

  getShop(shopCode: string) {
    return this.http.get(environment.api_url + 'shop/infos', {
      params: { shopCode: shopCode },
    });
  }

  getShopsOfUser(email): Observable<Shop[]> {
    return this.http.get<Shop[]>(environment.api_url + 'shop/infos');
  }

  updateScore(data) {
    return this.http.post(environment.api_url + 'shop/edit/score', data);
  }

  updateImport(data) {
    return this.http.post(environment.api_url + 'shop/edit/permission', data);
  }

  filterShops(shops: any[], filters: any[]): any[] {
    if (filters.length === 0) {
      return shops;
    }
    shops = shops.filter((shop: any) => {
      let valid = true;
      for (let filter of filters) {
        valid = this.handleStatusFilter(filter, valid, shop);
        valid = this.handleImportFilter(filter, valid, shop);
        valid = this.handleLastImportFilter(filter, valid, shop);
        valid = this.handleScoreMedia(filter, valid, shop);
        valid = this.handleScoreRef(filter, valid, shop);
        valid = this.handleScoreAd(filter, valid, shop);
      }
      return valid;
    });
    return shops;
  }

  private handleScoreRef(filter: any, valid: boolean, shop: any) {
    const truth = true;
    if (filter.name === 'scoreRef') {
      if (filter.value.length === 0) {
        valid = valid && truth;
      }
      if (filter.value.length === 2) {
        valid = valid && shop.scoreRef >= filter.value[0] && shop.scoreRef <= filter.value[1];
      }
    }
    return valid;
  }

  private handleNoValues(filter: any, valid: boolean) {
    const truth = true;
    if (filter.value.length === 0) {
      valid = valid && truth;
    }
    return valid;
  }

  private handleScoreAd(filter: any, valid: boolean, shop: any) {
    if (filter.name === 'scoreAd') {
      valid = this.handleNoValues(filter, valid);
      if (filter.value.length === 2) {
        valid = valid && shop.scoreAd >= filter.value[0] && shop.scoreAd <= filter.value[1];
      }
    }
    return valid;
  }

  private handleScoreMedia(filter: any, valid: boolean, shop: any) {
    if (filter.name === 'scoreMedia') {
      valid = this.handleNoValues(filter, valid);
      if (filter.value.length === 2) {
        valid = valid && shop.scoreMedia >= filter.value[0] && shop.scoreMedia <= filter.value[1];
      }
    }
    return valid;
  }

  private handleLastImportFilter(filter: any, valid: boolean, shop: any) {
    if (filter.name === 'lastImportDate') {
      valid = this.handleNoValues(filter, valid);
      if (filter.value.length === 2) {
        const input = shop.lastImportDate;
        const shopDate = parse(input, 'dd/MM/yyyy HH:mm', new Date());
        if(filter.value[0] == null) {
          valid = valid && isBefore(shopDate, filter.value[1]);
        } else if(filter.value[1] == null) {
          valid = valid && isAfter(shopDate, filter.value[0])
        } else {
          valid = valid && isAfter(shopDate, filter.value[0]) && isBefore(shopDate, filter.value[1]);
        }
      }
    }
    return valid;
  }

  private handleImportFilter(filter: any, valid: boolean, shop: any) {
    if (filter.name === 'shopCanImport') {
      valid = this.handleNoValues(filter, valid);
      if (filter.value.length > 0)
        valid = valid && filter.value.map((item: any) => item.data).includes(shop.shopCanImport);
    }
    return valid;
  }

  private handleStatusFilter(filter: any, valid: boolean, shop: any) {
    if (filter.name === 'shopStatus') {
      valid = this.handleNoValues(filter, valid);
      if (filter.value.length > 0)
        valid = valid && filter.value.map((item: any) => item.data).includes(shop.shopStatus.statusCode);
    }
    return valid;
  }

  setSelectedShop(shop: Shop) {
    this.localService.savePlainObjData(this.SHOP_KEY, shop);
    this.selectedShop.set(shop);
  }

  setSelectedShopCode(code): any {
    this.selectedShopCodeSignal.set(code);
    this.localService.setItem(this.SHOPCODE_KEY, code);
  }

  getSelectedShop(): any {
    const shops = this.userService.getUser()?.relatedShops;
    const code = this.getSelectedShopCode();
    const selectedShop = shops?.find((shop) => shop.shopCode === code);
    return selectedShop;
  }

  getSelectedShopCode(): any {
    const code = this.localService.getItem(this.SHOPCODE_KEY);
    if (code && code != 'undefined') {
      return code;
    }
    return this.userService.getUser()?.defaultShop?.shopCode;
  }

  uploadFile(file, code) {
    const formData: FormData = new FormData();
    formData.append('shopCode', code);
    formData.append('file', file);
    return this.http.post(environment.api_url + 'upload/test', formData, {
      responseType: 'text', // Specify text response type
    });
  }

  uploadInChunks(formData) {
   return this.http.post(environment.api_url +'upload', formData, {
      responseType: 'text'
    })
  }

  finalizeUpload(formData) {
    return this.http.post(environment.api_url +'upload/finalize', formData, { responseType: 'text' })
   }

  getAggregatorByShopCode(shopCode: string) {
    return this.http.get(environment.api_url + 'aggregators', {
      params: { shopCode: shopCode},
    });
  }
  getAllAggregators(shopCode) {
    return this.http.get(environment.api_url + 'aggregator/findAll', {params: {shopCode: shopCode}});
  }
  
  
  findAllAggregators() {
    return this.http.get(environment.api_url + 'aggregator/all');
  }

  associateAggregator(data) {
    return this.http.post(`${environment.api_url}aggregator/associate`, data)
  }

  getAttributesKapi(shopCode) {
    return this.http.get(`${environment.api_url}kpis/attributes`,  {params: {shopCode: shopCode}})
  }


  exportShops(data) {
    return this.http.post(`${environment.api_url}export/shop`, data, { responseType: 'blob', observe: 'response' })
  }

  exportUsers(data) {
    return this.http.post(`${environment.api_url}export/shop/user`, data, { responseType: 'blob', observe: 'response' })
  }

  exportShopHistory(data) {
    return this.http.post(`${environment.api_url}export/shop/history`, data, { responseType: 'blob', observe: 'response' })
  }

  getPaginatedShops(keyword : string, page: number) {
    return this.http.get(environment.api_url + 'shop/paginated', {params: {keyword: keyword, page: page}});
  }

  getPaginatedShopsWithFilers(data) {
    return this.http.post(environment.api_url + 'shop/all/paginated', data);
  }
}